import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  ENVIRONMENT_COLLECTION_MANAGEMENT_FEATURE_TOKEN,
  EnvironmentCollectionManagementFeatureInterface,
} from '@campus/environment';
import { FILTER_SERVICE_TOKEN, FilterServiceInterface } from '@campus/utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ItemToggledInCollectionInterface } from './interfaces/item-toggled-in-collection.interface';
import { ManageCollectionsDataInterface } from './interfaces/manage-collection-data.interface';
import { ManageCollectionItemInterface } from './interfaces/manage-collection-item.interface';

import '@diekeure/dcr-components/dcr-checkbox.js';
import '@diekeure/dcr-components/dcr-divider.js';
import '@diekeure/dcr-components/dcr-inline-icon-button.js';
import '@diekeure/dcr-components/dcr-section.js';
import '@diekeure/dcr-components/dcr-tonal-button.js';

@Component({
  standalone: false,
  selector: 'campus-manage-collection',
  templateUrl: './manage-collection.component.html',
  styleUrls: ['./manage-collection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageCollectionComponent implements AfterViewInit, OnInit, OnChanges {
  public recentLinkableItems: ManageCollectionItemInterface[];
  public linkableItems$ = new BehaviorSubject<ManageCollectionItemInterface[]>([]);
  public activePanel = -1;
  public listItems$: Observable<ManageCollectionItemInterface[]>;
  public filterValue = '';
  public useFilter: boolean;
  public newCollectionName: string;

  // needed to selected items after filtering
  private selectedIds: Set<number> = new Set<number>();
  private isDirty = false;

  @Output() closed = new EventEmitter<boolean>(); // Renamed from 'close' to avoid standard DOM event name
  @Output() selectionChanged = new EventEmitter<ItemToggledInCollectionInterface>();
  @Output() createCollection = new EventEmitter<{ label: string }>();

  @HostBinding('class.ui-manage-collection')
  get isManageCollectionClass() {
    return true;
  }

  private filterService: FilterServiceInterface = inject(FILTER_SERVICE_TOKEN);
  private router: Router = inject(Router);
  private environmentCollectionManagementFeatureToken: EnvironmentCollectionManagementFeatureInterface = inject(
    ENVIRONMENT_COLLECTION_MANAGEMENT_FEATURE_TOKEN
  );

  public ngOnInit() {
    this.useFilter = this.environmentCollectionManagementFeatureToken?.useFilter ?? false;
  }

  private _data: ManageCollectionsDataInterface;

  public get data(): ManageCollectionsDataInterface {
    return this._data;
  }

  public set data(value: ManageCollectionsDataInterface) {
    this._data = value;

    // Immediately make the linkable items available
    this.linkableItems$.next(this._data.linkableItems);

    // _data.recentItemIds is sorted new to old
    // we need to keep this sorting
    this.recentLinkableItems = Array.from(this._data.recentItemIds as Set<number>).reduce((acc, id) => {
      const recentItem = this._data.linkableItems.find((l) => l.id === id);
      if (recentItem) acc.push(recentItem);
      return acc;
    }, []);

    this.selectedIds = this._data.linkedItemIds;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && changes['data'].currentValue) {
      // The data setter already handles the logic, so we don't need to duplicate it here
      // This method is primarily for testing support
    }
  }

  public ngAfterViewInit() {
    // Initialize filtering
    this.listItems$ = this.linkableItems$.pipe(
      map((items) => this.filterService.filter(items, { label: this.filterValue }))
    );

    // Ensure the initial set of items is available
    if (this._data && this._data.linkableItems) {
      setTimeout(() => {
        this.linkableItems$.next(this._data.linkableItems);
      });
    }
  }

  public onFilterValueChange(value: string) {
    this.filterValue = value || '';
    this.linkableItems$.next(this._data.linkableItems);
  }

  public clearFilter() {
    this.filterValue = '';
    this.linkableItems$.next(this._data.linkableItems);
  }

  public isItemSelected(itemId: number): boolean {
    return this.selectedIds.has(itemId);
  }

  public onCheckboxChanged(checked: boolean, itemId: number) {
    this.isDirty = true;

    if (checked) {
      this.selectedIds.add(itemId);
    } else {
      this.selectedIds.delete(itemId);
    }

    // find changed item
    const changedItem = this.data.linkableItems.find((item) => item.id === itemId);

    this.selectionChanged.emit({
      items: this.data.items,
      relatedItem: changedItem,
      selected: checked,
    });
  }

  public onCreateCollectionClick(label: string) {
    this.isDirty = true;
    this.createCollection.emit({ label });
    this.newCollectionName = null;
  }

  public open() {
    this.activePanel = 0;
  }
  public onClose() {
    this.activePanel = -1;
  }
  public slideOutAnimationDone() {
    if (this.activePanel === -1) {
      this.closed.emit(this.isDirty);
    }
  }

  public navigateTo(event: MouseEvent, link: string) {
    event.stopPropagation(); // do not trigger a selection
    this.router.navigateByUrl(link);
    this.onClose();
  }

  public updateCollectionName(value: string) {
    this.newCollectionName = value;
  }
}
